import { useState } from 'react'
import {
  useSequencerStore,
  selectCurrentProject,
} from '@/stores/sequencerStore'
import type { AutomationLane } from '@busswerks/shared'
import { TICKS_PER_STEP } from '@busswerks/shared'
import {
  FaPlus as AddIcon,
  FaTrash as TrashIcon,
  FaCopy as DuplicateIcon,
} from 'react-icons/fa'

/**
 * Automation lane editor.
 *
 * Authoring surface for `Pattern.automation` lanes. Each lane
 * targets one `paramPath` and renders its breakpoints on an
 * SVG canvas. The resolver (`lib/automation.ts`) interpolates
 * values at fire time; pad-preset paths flow into the
 * instrument trigger path today, bus / project paths are still
 * data-only.
 *
 * ## Gestures
 *
 * - **Add lane**: type a `paramPath` + click "Lane". The
 *   `<datalist>` suggests common targets (`pad/0/preset/cutoff`,
 *   `bus/reverb/wet`, `project/bpm`).
 * - **Clear all lanes**: header button; confirms first.
 * - **Toggle enabled**: dot at the left of each lane header
 *   flips the lane's bypass flag without deleting.
 * - **Min / max**: number inputs in the header pin the canvas
 *   Y-axis to the param's real range. Empty max = auto-infer
 *   from existing points.
 * - **Duplicate lane**: copy icon in the header. Deep-copies
 *   points so the original + copy drift independently.
 * - **Delete lane**: trash icon in the header.
 *
 * ## Per-breakpoint
 *
 * - **Click empty canvas**: add a breakpoint at the clicked
 *   `{ tick, value }`.
 * - **Drag an existing breakpoint**: `onMouseDown` + window-level
 *   `mousemove` / `mouseup` updates `{ tick, value }` live.
 * - **Click without drag** (below a 2-tick / 2%-span threshold):
 *   removes the breakpoint.
 *
 * ## Playback visualization
 *
 * The lane's canvas renders a thin accent-colored cursor at the
 * scheduler's current tick when playback is running. Hidden
 * while stopped.
 */
interface AutomationLaneProps {
  fullScreen?: boolean
}

const DEFAULT_PARAM_PATHS: ReadonlyArray<string> = [
  'pad/0/preset/cutoff',
  'pad/0/preset/drive',
  'pad/0/preset/pitch',
  'bus/reverb/wet',
  'project/bpm',
]

export function AutomationLaneView({
  fullScreen = false,
}: AutomationLaneProps) {
  const project = useSequencerStore(selectCurrentProject)
  const addAutomationLane = useSequencerStore(s => s.addAutomationLane)
  const removeAutomationLane = useSequencerStore(s => s.removeAutomationLane)
  const setAutomationLaneEnabled = useSequencerStore(
    s => s.setAutomationLaneEnabled,
  )
  const addAutomationPoint = useSequencerStore(s => s.addAutomationPoint)
  const removeAutomationPointAtTick = useSequencerStore(
    s => s.removeAutomationPointAtTick,
  )
  const setAutomationLaneRange = useSequencerStore(
    s => s.setAutomationLaneRange,
  )
  const moveAutomationPoint = useSequencerStore(s => s.moveAutomationPoint)
  const duplicateAutomationLane = useSequencerStore(
    s => s.duplicateAutomationLane,
  )
  const currentStep = useSequencerStore(s => s.currentStep)
  const isPlaying = useSequencerStore(s => s.isPlaying)
  const clearAutomationLanes = useSequencerStore(s => s.clearAutomationLanes)

  const lanes: AutomationLane[] = project?.automation ?? []
  const stepCount = project?.stepCount ?? 16
  const totalTicks = stepCount * TICKS_PER_STEP

  const [newPath, setNewPath] = useState('')

  // In-pane: fill available vertical space. Full-screen: near-viewport.
  const heightClass = fullScreen
    ? 'h-[calc(100dvh-12rem)]'
    : 'flex-1 min-h-[18rem]'

  return (
    <div className="flex flex-col gap-2 flex-1 min-h-0">
      <form
        className="flex items-center gap-2"
        onSubmit={e => {
          e.preventDefault()
          const path = newPath.trim()
          if (!path) return
          addAutomationLane(path)
          setNewPath('')
        }}
      >
        <input
          type="text"
          list="automation-path-suggestions"
          value={newPath}
          onChange={e => setNewPath(e.target.value)}
          placeholder="pad/0/preset/cutoff"
          className="flex-1 bg-surface-sunken border border-brd-subtle rounded px-2 py-1 text-xs font-mono text-content-primary focus:outline-none focus:ring-1 focus:ring-[color:var(--accent-halo)]"
        />
        <datalist id="automation-path-suggestions">
          {DEFAULT_PARAM_PATHS.map(p => (
            <option key={p} value={p} />
          ))}
        </datalist>
        <button
          type="submit"
          disabled={!newPath.trim()}
          className="px-2 py-1 rounded border border-brd-subtle text-xs font-mono text-content-secondary hover:border-accent hover:text-accent disabled:opacity-40 transition-colors flex items-center gap-1"
        >
          <AddIcon size={9} /> Lane
        </button>
        {lanes.length > 0 && (
          <button
            type="button"
            onClick={() => {
              if (
                window.confirm(
                  `Remove all ${lanes.length} automation lane${lanes.length === 1 ? '' : 's'}?`,
                )
              ) {
                clearAutomationLanes()
              }
            }}
            className="px-2 py-1 rounded border border-brd-subtle text-xs font-mono text-content-tertiary hover:border-destructive hover:text-destructive transition-colors"
          >
            Clear all
          </button>
        )}
      </form>
      <div
        className={`${heightClass} overflow-auto rounded border border-brd-subtle bg-surface-sunken`}
        aria-label="Automation lanes"
      >
        {lanes.length === 0 ? (
          <div className="p-6 text-xs text-content-secondary">
            No automation lanes yet. Add one above — try{' '}
            <code className="font-mono">pad/0/preset/cutoff</code> to sweep a
            pad&apos;s filter over the pattern.
          </div>
        ) : (
          lanes.map(lane => (
            <LaneRow
              key={lane.id}
              lane={lane}
              totalTicks={totalTicks}
              onToggle={() =>
                setAutomationLaneEnabled(lane.id, lane.enabled === false)
              }
              onRemove={() => removeAutomationLane(lane.id)}
              onDuplicate={() => duplicateAutomationLane(lane.id)}
              onAddPoint={(tick, value) =>
                addAutomationPoint(lane.id, { tick, value })
              }
              onRemovePoint={tick => removeAutomationPointAtTick(lane.id, tick)}
              onMovePoint={(fromTick, to) =>
                moveAutomationPoint(lane.id, fromTick, to)
              }
              onRangeChange={(min, max) =>
                setAutomationLaneRange(lane.id, min, max)
              }
              playheadTick={
                isPlaying && currentStep >= 0
                  ? currentStep * TICKS_PER_STEP
                  : null
              }
            />
          ))
        )}
      </div>
    </div>
  )
}

interface LaneRowProps {
  lane: AutomationLane
  totalTicks: number
  onToggle: () => void
  onRemove: () => void
  onDuplicate: () => void
  onAddPoint: (tick: number, value: number) => void
  onRemovePoint: (tick: number) => void
  onMovePoint: (fromTick: number, to: { tick: number; value: number }) => void
  onRangeChange: (min: number | undefined, max: number | undefined) => void
  /** Scheduler playhead in ticks, or `null` when stopped. */
  playheadTick: number | null
}

function LaneRow({
  lane,
  totalTicks,
  onToggle,
  onRemove,
  onDuplicate,
  onAddPoint,
  onRemovePoint,
  onMovePoint,
  onRangeChange,
  playheadTick,
}: LaneRowProps) {
  // Canvas Y-axis range. Explicit min/max on the lane wins;
  // otherwise fall back to the existing inference (0..max of
  // absolute point values) so lanes that predate this field
  // keep their canvas behaviour.
  const minValue = lane.min ?? 0
  const maxValue =
    lane.max ??
    Math.max(1, ...lane.points.map(p => Math.abs(p.value - minValue)))
  const span = Math.max(1e-6, maxValue - minValue)

  const handleCanvasClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const relX = e.clientX - rect.left
    const relY = e.clientY - rect.top
    const tick = Math.max(
      0,
      Math.min(totalTicks - 1, Math.round((relX / rect.width) * totalTicks)),
    )
    const value = minValue + ((rect.height - relY) / rect.height) * span
    onAddPoint(tick, value)
  }

  return (
    <div className="border-b border-brd-subtle/40">
      <div className="flex items-center gap-2 px-2 py-1 bg-surface-raised/50 text-[10px] font-mono">
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={lane.enabled !== false}
          title={lane.enabled !== false ? 'Disable lane' : 'Enable lane'}
          className={`w-2 h-2 rounded-full ${
            lane.enabled !== false ? 'bg-accent' : 'bg-content-tertiary'
          }`}
        />
        <span className="text-content-primary flex-1 truncate">
          {lane.paramPath}
        </span>
        <label
          className="flex items-center gap-1 text-content-tertiary"
          title="Canvas minimum value"
        >
          min
          <input
            type="number"
            value={lane.min ?? ''}
            placeholder="0"
            onChange={e => {
              const v = e.target.value
              onRangeChange(v === '' ? undefined : Number(v), lane.max)
            }}
            className="w-14 bg-surface-sunken border border-brd-subtle rounded px-1 py-0.5 text-[10px] font-mono text-content-primary focus:outline-none focus:ring-1 focus:ring-[color:var(--accent-halo)]"
          />
        </label>
        <label
          className="flex items-center gap-1 text-content-tertiary"
          title="Canvas maximum value (auto-inferred when empty)"
        >
          max
          <input
            type="number"
            value={lane.max ?? ''}
            placeholder="auto"
            onChange={e => {
              const v = e.target.value
              onRangeChange(lane.min, v === '' ? undefined : Number(v))
            }}
            className="w-14 bg-surface-sunken border border-brd-subtle rounded px-1 py-0.5 text-[10px] font-mono text-content-primary focus:outline-none focus:ring-1 focus:ring-[color:var(--accent-halo)]"
          />
        </label>
        <span className="text-content-tertiary">
          {lane.points.length} pt{lane.points.length === 1 ? '' : 's'}
        </span>
        <button
          type="button"
          onClick={onDuplicate}
          aria-label={`Duplicate lane ${lane.paramPath}`}
          title="Duplicate this lane"
          className="text-content-tertiary hover:text-accent p-0.5"
        >
          <DuplicateIcon size={9} />
        </button>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove lane ${lane.paramPath}`}
          className="text-content-tertiary hover:text-destructive p-0.5"
        >
          <TrashIcon size={9} />
        </button>
      </div>
      <div className="relative h-16 bg-surface-base">
        {playheadTick !== null && (
          <div
            aria-hidden="true"
            className="absolute top-0 bottom-0 pointer-events-none z-10"
            style={{
              left: `${(playheadTick / totalTicks) * 100}%`,
              width: '1px',
              background: 'var(--accent)',
              opacity: 0.7,
            }}
          />
        )}
        <svg
          role="button"
          aria-label="Automation canvas — click to add breakpoint"
          className="absolute inset-0 w-full h-full cursor-crosshair"
          viewBox="0 0 100 40"
          preserveAspectRatio="none"
          onClick={handleCanvasClick}
        >
          <polyline
            fill="none"
            stroke="var(--accent)"
            strokeWidth="0.5"
            points={lane.points
              .map(p => {
                const x = (p.tick / totalTicks) * 100
                const y = 40 - ((p.value - minValue) / span) * 40
                return `${x},${y}`
              })
              .join(' ')}
          />
          {lane.points.map((p, i) => {
            const x = (p.tick / totalTicks) * 100
            const y = 40 - ((p.value - minValue) / span) * 40
            const handleMouseDown = (e: React.MouseEvent<SVGCircleElement>) => {
              e.stopPropagation()
              const svg = e.currentTarget.ownerSVGElement
              if (!svg) return
              const rect = svg.getBoundingClientRect()
              const startTick = p.tick
              let moved = false
              let latestTick = startTick
              let latestValue = p.value
              const onMove = (mv: MouseEvent) => {
                const relX = Math.max(
                  0,
                  Math.min(rect.width, mv.clientX - rect.left),
                )
                const relY = Math.max(
                  0,
                  Math.min(rect.height, mv.clientY - rect.top),
                )
                const tick = Math.max(
                  0,
                  Math.min(
                    totalTicks - 1,
                    Math.round((relX / rect.width) * totalTicks),
                  ),
                )
                const value =
                  minValue + ((rect.height - relY) / rect.height) * span
                if (
                  !moved &&
                  (Math.abs(tick - startTick) > 2 ||
                    Math.abs(value - p.value) > span * 0.02)
                ) {
                  moved = true
                }
                latestTick = tick
                latestValue = value
                if (moved) onMovePoint(startTick, { tick, value })
              }
              const onUp = () => {
                window.removeEventListener('mousemove', onMove)
                window.removeEventListener('mouseup', onUp)
                if (!moved) {
                  // No drag ⇒ treat as click = remove.
                  onRemovePoint(startTick)
                } else {
                  // Commit final position defensively.
                  onMovePoint(startTick, {
                    tick: latestTick,
                    value: latestValue,
                  })
                }
              }
              window.addEventListener('mousemove', onMove)
              window.addEventListener('mouseup', onUp)
            }
            return (
              <circle
                key={i}
                cx={x}
                cy={y}
                r="1"
                fill="var(--accent)"
                onMouseDown={handleMouseDown}
                style={{ cursor: 'grab' }}
              >
                <title>{`tick ${p.tick} → ${p.value.toFixed(2)} · drag to move · click to remove`}</title>
              </circle>
            )
          })}
        </svg>
      </div>
    </div>
  )
}
